import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import FeedbackIcon from '@mui/icons-material/Feedback';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import { getStakeholderInterviews, saveStakeholderInterviews, fetchStakeholderInterviewsFromSOW } from '../../utils/discoveryPhaseService';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

const StakeholderInterviewsPage = () => {
  const { projectId } = useParams();
  const { project } = useOutletContext();
  
  const [interviews, setInterviews] = useState({
    summary: '',
    interviewSessions: [],
    keyFindings: [],
    stakeholderFeedback: []
  });
  
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [editingInterviews, setEditingInterviews] = useState({
    summary: '',
    interviewSessions: [],
    keyFindings: [],
    stakeholderFeedback: []
  });
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogItem, setDialogItem] = useState({});
  const [dialogItemIndex, setDialogItemIndex] = useState(null);
  
  // Load stakeholder interviews data from database when component mounts
  useEffect(() => {
    if (projectId) {
      loadStakeholderInterviewsFromDB();
    }
  }, [projectId]);
  
  // Load stakeholder interviews from database
  const loadStakeholderInterviewsFromDB = async () => {
    setLoading(true);
    try {
      const interviewsData = await getStakeholderInterviews(projectId);
      if (interviewsData) {
        setInterviews(interviewsData);
        setEditingInterviews(interviewsData);
      }
    } catch (error) {
      console.error('Error loading stakeholder interviews:', error);
      showSnackbar('Error loading stakeholder interviews data', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Save stakeholder interviews to database
  const handleSave = async () => {
    setLoading(true);
    try {
      await saveStakeholderInterviews(projectId, editingInterviews);
      setInterviews(editingInterviews);
      setEditMode(false);
      showSnackbar('Stakeholder interviews saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving stakeholder interviews:', error);
      showSnackbar('Error saving stakeholder interviews', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch stakeholder interviews from project documents using AI
  const handleFetchFromSOW = async () => {
    setAiLoading(true);
    try {
      const response = await fetchStakeholderInterviewsFromSOW(projectId);
      if (response.data) {
        setEditingInterviews(response.data);
        setInterviews(response.data);
        showSnackbar('Stakeholder interviews generated from project documents successfully!', 'success');
      }
    } catch (error) {
      console.error('Error fetching stakeholder interviews from project documents:', error);
      showSnackbar('Error generating stakeholder interviews from project documents', 'error');
    } finally {
      setAiLoading(false);
    }
  };
  
  // Dialog functions
  const handleOpenDialog = (type, item = null, index = null) => {
    setDialogType(type);
    
    if (type === 'interviewSessions') {
      setDialogItem(item || { 
        stakeholder: '', 
        role: '', 
        date: '', 
        duration: '', 
        location: '', 
        topics: [], 
        notes: '' 
      });
    } else if (type === 'keyFindings') {
      setDialogItem(item || { 
        finding: '', 
        category: '', 
        impact: '', 
        priority: 'Medium', 
        source: '', 
        actionRequired: '' 
      });
    } else if (type === 'stakeholderFeedback') {
      setDialogItem(item || { 
        stakeholder: '', 
        role: '', 
        feedback: '', 
        sentiment: 'Neutral', 
        category: '', 
        actionItems: [] 
      });
    }
    
    setDialogItemIndex(index);
    setOpenDialog(true);
  };
  
  const handleSaveDialog = () => {
    const newData = { ...editingInterviews };
    
    if (dialogItemIndex !== null) {
      // Edit existing item
      newData[dialogType][dialogItemIndex] = dialogItem;
    } else {
      // Add new item
      newData[dialogType].push(dialogItem);
    }
    
    setEditingInterviews(newData);
    setOpenDialog(false);
  };
  
  const handleDeleteItem = (type, index) => {
    const newData = { ...editingInterviews };
    newData[type].splice(index, 1);
    setEditingInterviews(newData);
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const renderInterviewSessionCard = (session, index) => {
    return (
      <Card key={index} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
            <Typography variant="h6" gutterBottom>
              {session.stakeholder}
            </Typography>
            <Chip 
              label={session.role} 
              color="primary"
              size="small"
            />
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Date:</strong> {session.date}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Duration:</strong> {session.duration}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Location:</strong> {session.location}
              </Typography>
            </Grid>
          </Grid>
          
          {session.topics && session.topics.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Topics Discussed:</strong>
              </Typography>
              <List dense>
                {session.topics.map((topic, idx) => (
                  <ListItem key={idx} sx={{ pl: 2 }}>
                    <ListItemText primary={`• ${topic}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          {session.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Notes:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {session.notes}
              </Typography>
            </Box>
          )}
        </CardContent>
        {editMode && (
          <CardActions>
            <IconButton 
              size="small" 
              onClick={() => handleOpenDialog('interviewSessions', session, index)}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleDeleteItem('interviewSessions', index)}
            >
              <DeleteIcon />
            </IconButton>
          </CardActions>
        )}
      </Card>
    );
  };
  
  const renderKeyFindingCard = (finding, index) => {
    const getPriorityColor = (priority) => {
      switch (priority) {
        case 'High': return 'error';
        case 'Medium': return 'warning';
        case 'Low': return 'success';
        default: return 'default';
      }
    };
    
    return (
      <Card key={index} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
            <Typography variant="h6" gutterBottom>
              {finding.finding}
            </Typography>
            <Chip 
              label={finding.priority} 
              color={getPriorityColor(finding.priority)}
              size="small"
            />
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Category:</strong> {finding.category}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Source:</strong> {finding.source}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Impact:</strong> {finding.impact}
              </Typography>
            </Grid>
            {finding.actionRequired && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Action Required:</strong> {finding.actionRequired}
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
        {editMode && (
          <CardActions>
            <IconButton 
              size="small" 
              onClick={() => handleOpenDialog('keyFindings', finding, index)}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleDeleteItem('keyFindings', index)}
            >
              <DeleteIcon />
            </IconButton>
          </CardActions>
        )}
      </Card>
    );
  };
  
  const renderStakeholderFeedbackCard = (feedback, index) => {
    const getSentimentColor = (sentiment) => {
      switch (sentiment) {
        case 'Positive': return 'success';
        case 'Negative': return 'error';
        case 'Neutral': return 'default';
        default: return 'default';
      }
    };
    
    return (
      <Card key={index} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
            <Typography variant="h6" gutterBottom>
              {feedback.stakeholder}
            </Typography>
            <Chip 
              label={feedback.sentiment} 
              color={getSentimentColor(feedback.sentiment)}
              size="small"
            />
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Role:</strong> {feedback.role}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Category:</strong> {feedback.category}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" paragraph>
                <strong>Feedback:</strong> {feedback.feedback}
              </Typography>
            </Grid>
          </Grid>
          
          {feedback.actionItems && feedback.actionItems.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Action Items:</strong>
              </Typography>
              <List dense>
                {feedback.actionItems.map((action, idx) => (
                  <ListItem key={idx} sx={{ pl: 2 }}>
                    <ListItemText primary={`• ${action}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </CardContent>
        {editMode && (
          <CardActions>
            <IconButton 
              size="small" 
              onClick={() => handleOpenDialog('stakeholderFeedback', feedback, index)}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleDeleteItem('stakeholderFeedback', index)}
            >
              <DeleteIcon />
            </IconButton>
          </CardActions>
        )}
      </Card>
    );
  };
  
  const renderSection = (type, title, items, renderCard, icon) => {
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            {icon}
            <Typography variant="h6" sx={{ ml: 1 }}>{title}</Typography>
            <Chip 
              label={items.length} 
              size="small" 
              sx={{ ml: 2 }} 
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {editMode && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog(type)}
                sx={{ mb: 2 }}
              >
                Add {title.slice(0, -1)}
              </Button>
            )}
            {items.map((item, index) => renderCard(item, index))}
            {items.length === 0 && (
              <Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
                No {title.toLowerCase()} defined yet.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };
  
  if (loading && !interviews.summary) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <SectionTitle variant="h4">
            <GroupIcon />
            Stakeholder Interviews
          </SectionTitle>
          <Box>
            {!editMode ? (
              <>
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleFetchFromSOW}
                  disabled={aiLoading}
                  sx={{ mr: 1, backgroundColor: '#F5821F' }}
                >
                  {aiLoading ? <CircularProgress size={20} /> : 'Fetch From Project Documents'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(true)}
                >
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={loading}
                  sx={{ mr: 1 }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    setEditMode(false);
                    setEditingInterviews(interviews);
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Conduct and document stakeholder interviews and findings for {project?.name}.
        </Typography>
      </Paper>
      
      {/* Overview Section */}
      <Accordion expanded={true}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Summary</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {editMode ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Stakeholder Interviews Summary"
              value={editingInterviews.summary}
              onChange={(e) => setEditingInterviews({
                ...editingInterviews,
                summary: e.target.value
              })}
            />
          ) : (
            <Typography>
              {interviews.summary || 'No summary available. Click "Edit" to add content or "Fetch From Project Documents" to generate from your project documents.'}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
      
      {/* Interview Sessions */}
      {renderSection('interviewSessions', 'Interview Sessions', editingInterviews.interviewSessions, renderInterviewSessionCard, <RecordVoiceOverIcon />)}
      
      {/* Key Findings */}
      {renderSection('keyFindings', 'Key Findings', editingInterviews.keyFindings, renderKeyFindingCard, <FindInPageIcon />)}
      
      {/* Stakeholder Feedback */}
      {renderSection('stakeholderFeedback', 'Stakeholder Feedback', editingInterviews.stakeholderFeedback, renderStakeholderFeedbackCard, <FeedbackIcon />)}
      
      {/* Dialog for adding/editing items */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogItemIndex !== null ? 'Edit' : 'Add'} {dialogType === 'interviewSessions' ? 'Interview Session' : dialogType === 'keyFindings' ? 'Key Finding' : 'Stakeholder Feedback'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {dialogType === 'interviewSessions' && (
              <>
                <TextField
                  fullWidth
                  label="Stakeholder Name"
                  value={dialogItem.stakeholder || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, stakeholder: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Role"
                  value={dialogItem.role || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, role: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={dialogItem.date || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Duration"
                  value={dialogItem.duration || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, duration: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Location"
                  value={dialogItem.location || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, location: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Notes"
                  value={dialogItem.notes || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, notes: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </>
            )}
            
            {dialogType === 'keyFindings' && (
              <>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Finding"
                  value={dialogItem.finding || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, finding: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Category"
                  value={dialogItem.category || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, category: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Impact"
                  value={dialogItem.impact || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, impact: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  select
                  label="Priority"
                  value={dialogItem.priority || 'Medium'}
                  onChange={(e) => setDialogItem({ ...dialogItem, priority: e.target.value })}
                  SelectProps={{ native: true }}
                  sx={{ mb: 2 }}
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </TextField>
                <TextField
                  fullWidth
                  label="Source"
                  value={dialogItem.source || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, source: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Action Required"
                  value={dialogItem.actionRequired || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, actionRequired: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </>
            )}
            
            {dialogType === 'stakeholderFeedback' && (
              <>
                <TextField
                  fullWidth
                  label="Stakeholder Name"
                  value={dialogItem.stakeholder || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, stakeholder: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Role"
                  value={dialogItem.role || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, role: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Feedback"
                  value={dialogItem.feedback || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, feedback: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  select
                  label="Sentiment"
                  value={dialogItem.sentiment || 'Neutral'}
                  onChange={(e) => setDialogItem({ ...dialogItem, sentiment: e.target.value })}
                  SelectProps={{ native: true }}
                  sx={{ mb: 2 }}
                >
                  <option value="Positive">Positive</option>
                  <option value="Neutral">Neutral</option>
                  <option value="Negative">Negative</option>
                </TextField>
                <TextField
                  fullWidth
                  label="Category"
                  value={dialogItem.category || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, category: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveDialog} variant="contained">
            {dialogItemIndex !== null ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StakeholderInterviewsPage;